import { Button, Grid, Stack, Title } from '@mantine/core';
import moment from 'moment';
import { useEffect } from 'react';

import { Declaration, EORIGIN_DATE_FORMAT } from '@e-origin/shared';

import { DatePickerInput, LeavePageNotification, StickyContainer, TextInput } from '@components';
import { BuyerForm } from '../BuyerForm';
import { SellerForm } from '../SellerForm';
import { useDeclarationViewDV1Form } from './useDeclarationViewDV1Form';

interface DeclarationViewDV1Props {
  editDisabled: boolean;
  declaration: Declaration;
}

export const DeclarationViewDV1: React.FC<DeclarationViewDV1Props> = ({ declaration, editDisabled }) => {
  const { form, onSubmit, isSaving, buildFormValues } = useDeclarationViewDV1Form({ declaration });

  useEffect(() => {
    form.setValues(buildFormValues(declaration));
    form.resetDirty({});
  }, [declaration]);

  return (
    <>
      <LeavePageNotification hasUnsavedChanges={form.isDirty()} />

      <form onSubmit={form.onSubmit(onSubmit)} style={{ paddingRight: '30%' }}>
        <SellerForm form={form} editDisabled={editDisabled} />
        <BuyerForm form={form} editDisabled={editDisabled} />
        <Stack mb="xl">
          <Title order={5} fw={600}>
            Contract
          </Title>

          <Grid>
            <Grid.Col span={6}>
              <TextInput {...form.getInputProps('dv1.contract.contractNumber')} label="Identification Number" disabled={editDisabled} />
            </Grid.Col>
            <Grid.Col span={6}>
              <DatePickerInput
                {...form.getInputProps('dv1.contract.contractDate')}
                label={`Contract Date (${EORIGIN_DATE_FORMAT})`}
                value={
                  form.values.dv1.contract.contractDate && moment(form.values.dv1.contract.contractDate, EORIGIN_DATE_FORMAT).isValid()
                    ? moment(form.values.dv1.contract.contractDate, EORIGIN_DATE_FORMAT).toDate()
                    : null
                }
                onChange={(value: Date) =>
                  form.setFieldValue('dv1.contract.contractDate', value ? moment(value, EORIGIN_DATE_FORMAT).format(EORIGIN_DATE_FORMAT) : null)
                }
                clearable
              />
            </Grid.Col>
          </Grid>
        </Stack>

        <Stack mb="xl">
          <Title order={5} fw={600}>
            Relation
          </Title>

          <Grid>
            <Grid.Col span={6}>
              <TextInput {...form.getInputProps('dv1.BuyerSellerRelation.relation')} label="Relation" disabled={editDisabled} />
            </Grid.Col>
          </Grid>
        </Stack>

        <Stack mb="xl">
          <Title order={5} fw={600}>
            Restrictions
          </Title>

          <Grid>
            <Grid.Col span={6}>
              <TextInput {...form.getInputProps('dv1.RestrictionsAndConditions.restrictions')} label="Restrictions" disabled={editDisabled} />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput {...form.getInputProps('dv1.RestrictionsAndConditions.consideration')} label="Consideration" disabled={editDisabled} />
            </Grid.Col>
          </Grid>
        </Stack>

        <Stack mb="xl">
          <Title order={5} fw={600}>
            Royalties and Resale
          </Title>

          <Grid>
            <Grid.Col span={6}>
              <TextInput {...form.getInputProps('dv1.RoyaltiesAndResale.royalties')} label="Royalties" disabled={editDisabled} />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput {...form.getInputProps('dv1.RestrictionsAndConditions.resale')} label="Resale" disabled={editDisabled} />
            </Grid.Col>
          </Grid>
        </Stack>

        <Stack mb="xl">
          <Title order={5} fw={600}>
            Signature
          </Title>

          <Grid>
            <Grid.Col span={6}>
              <TextInput {...form.getInputProps('dv1.signature.place')} label="Place" disabled={editDisabled} />
            </Grid.Col>
          </Grid>
        </Stack>

        <StickyContainer noPl>
          <Button data-pw="submit-btn" type="submit" key="save-modal-btn" variant="filled" loading={isSaving} size="lg" my="xl" disabled={editDisabled}>
            Save
          </Button>
        </StickyContainer>
      </form>
    </>
  );
};
